<template>
  <div class="warehouse-transfer-request-view pa-4">
    <!-- 1. Header / Tarjeta de Título Neutra -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center justify-space-between py-4 px-5 flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="primary" large class="mr-3">mdi-swap-horizontal-bold</v-icon>
          <div>
            <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
              Solicitud de Transferencia entre Almacenes
            </h2>
            <span class="text-caption grey--text text--darken-2">
              Registro y seguimiento de necesidades de materiales para el área de producción.
            </span>
          </div>
        </div>

        <v-btn
          color="primary"
          class="rounded-lg font-weight-bold text-none px-4"
          elevation="0"
          @click="openCreateModal"
        >
          <v-icon left small>mdi-plus-circle-outline</v-icon>
          Nueva Solicitud
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- 2. Filtros de Búsqueda -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros de Búsqueda
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="6" md="3">
            <s-select-area label="Área" v-model="filters.AreID" full clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-date label="Fecha Inicio" v-model="filters.BeginDate" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-date label="Fecha Fin" v-model="filters.EndDate" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-select-definition :def="1062" label="Turno" v-model="filters.TypeShift" clearable />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <s-select
              label="Estado"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              v-model="filters.WtrStatus"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="12" md="1" class="d-flex align-center">
            <v-btn
              block
              color="primary"
              class="rounded-lg font-weight-bold text-none"
              elevation="0"
              :loading="loading"
              @click="loadData"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 3. Grilla de Solicitudes Registradas -->
    <v-card class="rounded-xl border-light" outlined elevation="0">
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        dense
        class="elevation-0"
        no-data-text="No se encontraron solicitudes registradas"
      >
        <template v-slot:[`item.WtrDate`]="{ item }">
          {{ formatDate(item.WtrDate) }}
        </template>
        <template v-slot:[`item.WtrStatus`]="{ item }">
          <v-chip x-small :color="getStatusColor(item.WtrStatus)" class="font-weight-bold" label>
            {{ item.WtrStatusName }}
          </v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn icon small color="primary" v-on="on" @click="viewDetail(item)">
                <v-icon small>mdi-eye-outline</v-icon>
              </v-btn>
            </template>
            <span>Ver Detalle</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal de Creación de Solicitud -->
    <warehouse-transfer-request-modal
      v-model="dialogCreate"
      :warehouses="warehouses"
      @saved="loadData"
    />

    <!-- Diálogo de Visualización de Detalle -->
    <v-dialog v-model="dialogDetail" max-width="850" persistent>
      <v-card class="rounded-xl border-light" outlined elevation="0">
        <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
            <span class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
              Detalle de Solicitud: {{ selectedRequest ? selectedRequest.WtrDocumentNumber : '' }}
            </span>
          </div>
          <v-btn icon small @click="dialogDetail = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4 px-5">
          <v-row dense class="mb-3">
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Área:</span>
              <div class="font-weight-bold">{{ selectedRequest ? selectedRequest.AreName : '-' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Fecha:</span>
              <div class="font-weight-bold">{{ selectedRequest ? formatDate(selectedRequest.WtrDate) : '-' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Turno:</span>
              <div class="font-weight-bold">{{ selectedRequest ? selectedRequest.TypeShiftName : '-' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Encargado:</span>
              <div class="font-weight-bold">{{ selectedRequest ? selectedRequest.WtrManager : '-' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <span class="text-caption grey--text text--darken-2">Estado:</span>
              <div>
                <v-chip x-small :color="getStatusColor(selectedRequest ? selectedRequest.WtrStatus : 0)" label>
                  {{ selectedRequest ? selectedRequest.WtrStatusName : '-' }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" sm="4" v-if="selectedRequest && selectedRequest.FromWhsCode">
              <span class="text-caption grey--text text--darken-2">Almacén de Origen:</span>
              <div class="font-weight-bold">{{ getWarehouseName(selectedRequest.FromWhsCode) }}</div>
            </v-col>
            <v-col cols="12" sm="4" v-if="selectedRequest && selectedRequest.ToWhsCode">
              <span class="text-caption grey--text text--darken-2">Almacén de Destino:</span>
              <div class="font-weight-bold">{{ getWarehouseName(selectedRequest.ToWhsCode) }}</div>
            </v-col>
            <v-col cols="12" sm="4" v-if="selectedRequest && selectedRequest.WtrSapDocEntry">
              <span class="text-caption grey--text text--darken-2">DocNum SAP:</span>
              <div class="font-weight-bold text-primary">{{ selectedRequest.WtrSapDocEntry }}</div>
            </v-col>
          </v-row>

          <v-data-table
            :headers="detailHeaders"
            :items="detailItems"
            :loading="loadingDetail"
            dense
            hide-default-footer
            :items-per-page="-1"
            class="border-light rounded-lg"
          >
            <template v-slot:[`item.WtdQuantityRequested`]="{ item }">
              {{ Number(item.WtdQuantityRequested).toFixed(2) }}
            </template>
            <template v-slot:[`item.WtdQuantityServed`]="{ item }">
              <span v-if="item.WtdQuantityServed != null" class="font-weight-bold text--success">
                {{ Number(item.WtdQuantityServed).toFixed(2) }}
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:[`item.FromWhsCode`]="{ item }">
              <span>{{ getWarehouseName(item.FromWhsCode) }}</span>
            </template>
            <template v-slot:[`item.ToWhsCode`]="{ item }">
              <span class="font-weight-medium">{{ getWarehouseName(item.ToWhsCode) }}</span>
            </template>
          </v-data-table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-5">
          <v-spacer></v-spacer>
          <v-btn text class="text-none rounded-lg" @click="dialogDetail = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SSelectArea from "@/components/Utils/SSelectArea.vue";
import SDate from "@/components/Utils/SDate.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SSelect from "@/components/Utils/SSelect.vue";
import WarehouseTransferRequestModal from "./WarehouseTransferRequestModal.vue";
import _sWarehouseTransfer from "@/services/Logistics/WarehouseTransferService";

export default {
  name: "WarehouseTransferRequest",
  components: {
    SSelectArea,
    SDate,
    SSelectDefinition,
    SSelect,
    WarehouseTransferRequestModal,
  },
  data() {
    return {
      loading: false,
      dialogCreate: false,
      dialogDetail: false,
      loadingDetail: false,
      warehouses: [],
      selectedRequest: null,
      detailItems: [],
      filters: {
        AreID: null,
        BeginDate: null,
        EndDate: null,
        TypeShift: null,
        WtrStatus: null,
        Search: "",
      },
      statusOptions: [
        { text: "TODOS", value: null },
        { text: "PENDIENTE", value: 1 },
        { text: "ATENDIDO", value: 2 },
        { text: "RECHAZADO", value: 3 },
      ],
      items: [],
      headers: [
        { text: "N° Solicitud", value: "WtrDocumentNumber", width: 130, sortable: true },
        { text: "Fecha", value: "WtrDate", width: 110, sortable: true },
        { text: "Área", value: "AreName", sortable: true },
        { text: "Turno", value: "TypeShiftName", width: 110, sortable: true },
        { text: "Encargado", value: "WtrManager", sortable: true },
        { text: "Ítems", value: "TotalItems", width: 80, align: "center", sortable: true },
        { text: "Estado", value: "WtrStatus", width: 120, align: "center", sortable: true },
        { text: "DocNum SAP", value: "WtrSapDocEntry", width: 120, align: "center", sortable: true },
        { text: "Acciones", value: "actions", width: 90, align: "center", sortable: false },
      ],
      detailHeaders: [
        { text: "Línea", value: "WtdNumLine", width: 70, align: "center" },
        { text: "Código", value: "ArtCode", width: 110 },
        { text: "Descripción", value: "WtdDescription" },
        { text: "U.M.", value: "UnitMeasurement", width: 70, align: "center" },
        { text: "Cant. Solicitada", value: "WtdQuantityRequested", width: 130, align: "right" },
        { text: "Cant. Atendida", value: "WtdQuantityServed", width: 130, align: "right" },
        { text: "Almacén Origen", value: "FromWhsCode", width: 150 },
        { text: "Almacén Destino", value: "ToWhsCode", width: 150 },
      ],
    };
  },
  created() {
    this.loadWarehouses();
    this.loadData();
  },
  methods: {
    loadWarehouses() {
      _sWarehouseTransfer.getWarehouses().then((resp) => {
        this.warehouses = resp.data || [];
      });
    },
    loadData() {
      this.loading = true;
      const filterPayload = { ...this.filters };
      // Si no es admin, solo debe ver sus propias solicitudes
      if (!this.$fun.isAdmin()) {
        filterPayload.UsrCreateID = this.$fun.getUserID();
      }

      _sWarehouseTransfer
        .list(filterPayload, this.$fun.getUserID())
        .then((resp) => {
          this.items = resp.data || [];
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    openCreateModal() {
      this.dialogCreate = true;
    },
    viewDetail(item) {
      this.selectedRequest = item;
      this.dialogDetail = true;
      this.loadingDetail = true;
      _sWarehouseTransfer
        .detail({ WtrID: item.WtrID }, this.$fun.getUserID())
        .then((resp) => {
          this.detailItems = resp.data || [];
          this.loadingDetail = false;
        })
        .catch(() => {
          this.loadingDetail = false;
        });
    },
    getStatusColor(status) {
      switch (status) {
        case 1:
          return "warning"; // Pendiente
        case 2:
          return "success"; // Atendido
        case 3:
          return "error"; // Rechazado
        default:
          return "grey";
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return dateStr.substring(0, 10);
    },
    getWarehouseName(code) {
      if (!code) return "-";
      const strCode = String(code).trim();
      const wh = this.warehouses.find(
        (w) =>
          String(w.WhsCode).trim() === strCode ||
          String(w.WhsCode).trim().padStart(2, "0") === strCode.padStart(2, "0")
      );
      return wh ? `${wh.WhsCode} - ${wh.WhsName}` : code;
    },
  },
};
</script>

